import type { ReactNode } from "react";
import type { ButtonProps, ButtonSize, ButtonStyle } from "ui/buttons/Button/Button";
import LoadingDots from "ui/components/LoadingDots";
import cx from "ui/cx";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./IconButton.module.scss";

export type IconButtonSize = Exclude<ButtonSize, "none">;
export type IconButtonStyle = Exclude<ButtonStyle, "link" | "green" | "remove">;

export interface IconButtonProps
  extends Omit<ButtonProps, "aria-label" | "fullWidth" | "pendingClassName" | "btnStyle" | "btnSize"> {
  ariaLabel: string;
  children: ReactNode;
  btnStyle?: IconButtonStyle;
  btnSize?: IconButtonSize;
}

export default function IconButton({
  children,
  ariaLabel,
  btnStyle = "secondary",
  btnSize = "medium",
  isPending = false,
  pendingChildren,
  onClick,
  title,
  disabled,
  dataTestId,
  tooltip,
  ...btnProps
}: IconButtonProps) {
  const classNames = cx(
    {
      styles,
      iconButton: true,
      [btnStyle]: true,
      [btnSize]: true,
      pending: isPending,
    },
    btnProps.className ?? "",
  );

  return wrapWithTooltip(
    <button
      {...btnProps}
      type={btnProps.type ?? "button"}
      onClick={onClick}
      className={classNames}
      disabled={disabled || isPending}
      title={disabled ? title : undefined}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {isPending ? pendingChildren ?? <LoadingDots /> : children}
    </button>,
    tooltip,
  );
}
